import PersonIcon from "@mui/icons-material/Person";
import {
  formatAppointmentPurpose,
  formatPatientTypeLabel,
  patientMobileFromItem,
  patientTypeLabelFromItem,
  PATIENT_TYPE_STYLES,
  purposeColorFromItem,
} from "../../viewappointment/components/appointmentViewShared";
import { getOppositeColor } from "../../../../utils/utils";

function slotTextColor(bgColor: string, colorByPurpose?: boolean): string {
  if (!colorByPurpose || !bgColor) return "#ffffff";
  try {
    return getOppositeColor(bgColor);
  } catch {
    return "#ffffff";
  }
}

/** Soft pink for completed calendar slots. */
const COMPLETED_SLOT_BG = "#f9a8d4";
const COMPLETED_SLOT_TEXT = "#9d174d";

export default function AppointmentEvent({
  appointment,
  isMonthView,
  isDayView,
  colorByPurpose,
  reschedule,
  onOnlineAppointmentClick,
}: {
  reschedule?: any;
  appointment: any;
  isMonthView?: boolean;
  isDayView?: boolean;
  /** When a doctor is selected, color slots by purpose instead of doctor color. */
  colorByPurpose?: boolean;
  onOnlineAppointmentClick?: any;
}) {
  const purposeColor = purposeColorFromItem(appointment);
  const isCompleted = appointment?.status === "completed";
  const bgColor = isCompleted
    ? COMPLETED_SLOT_BG
    : colorByPurpose && purposeColor
      ? purposeColor
      : appointment?.color || "#6366f1";
  const textColor = isCompleted
    ? COMPLETED_SLOT_TEXT
    : slotTextColor(
        bgColor,
        Boolean(colorByPurpose && purposeColor)
      );
  const mutedTextColor = textColor;
  const isDefault = appointment?.type === "default" && appointment?.type;
  const isOnline = appointment?.type === "online";
  const purpose = formatAppointmentPurpose(appointment);
  const showPurpose = !isMonthView && purpose && purpose !== "N/A";
  const phone = patientMobileFromItem(appointment);
  const showPhone = Boolean(phone && phone !== "—");
  const patientTypeLabel = patientTypeLabelFromItem(appointment);
  const patientTypeShort = formatPatientTypeLabel(patientTypeLabel);
  const showDoctor = !isMonthView && Boolean(appointment?.dentist);
  const overlapCount = Number(appointment?._overlapCount || 0);
  const showOverlapBadge = isDayView && overlapCount > 0;

  const metaParts = [
    showPhone ? phone : null,
    showPurpose ? purpose : null,
    showDoctor ? appointment?.dentist : null,
  ].filter(Boolean) as string[];

  const handleClick = () => {
    if (isOnline && !!onOnlineAppointmentClick && !appointment?.added) {
      onOnlineAppointmentClick(appointment);
    } else {
      reschedule(appointment);
    }
  };

  const title = [
    appointment?.patientName,
    showPhone ? phone : null,
    patientTypeShort,
    showPurpose ? purpose : null,
    appointment?.dentist,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      onClick={handleClick}
      className="h-full w-full px-1.5 py-0.5 rounded-lg cursor-pointer transition-all hover:opacity-90 hover:scale-[1.02] overflow-hidden"
      style={{
        backgroundColor: bgColor,
        color: textColor,
        border: isDefault ? "none" : "2px solid rgba(0,0,0,0.2)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
      title={title}
    >
      {isDayView && !isMonthView ? (
        <div className="flex items-center gap-1.5 h-full min-h-0 min-w-0 leading-tight">
          <PersonIcon
            sx={{ fontSize: 11, color: mutedTextColor }}
            className="shrink-0 opacity-80"
          />
          <span
            className="text-[11px] font-semibold truncate shrink-0 max-w-[40%]"
            style={{ color: textColor }}
          >
            {appointment?.patientName}
          </span>
          <span
            className={`inline-flex items-center rounded px-1 py-px text-[9px] font-bold uppercase tracking-wide border shrink-0 leading-none ${
              PATIENT_TYPE_STYLES[patientTypeLabel] ||
              PATIENT_TYPE_STYLES["Existing Patient"]
            }`}
          >
            {patientTypeShort}
          </span>
          {metaParts.length > 0 ? (
            <span
              className="min-w-0 flex-1 truncate text-[9px] font-medium opacity-95"
              style={{ color: textColor }}
            >
              {metaParts.join(" · ")}
            </span>
          ) : null}
          {showOverlapBadge ? (
            <span
              className="inline-flex shrink-0 items-center rounded-full border border-white/40 bg-black/15 px-1.5 py-px text-[9px] font-semibold leading-none"
              style={{ color: textColor }}
              title={`Overlaps with ${overlapCount} more appointment${
                overlapCount === 1 ? "" : "s"
              }`}
            >
              +{overlapCount}
            </span>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col justify-center gap-0 h-full min-h-0 leading-tight">
          <div className="flex items-center gap-1 min-w-0 shrink-0">
            <PersonIcon
              sx={{ fontSize: 11, color: mutedTextColor }}
              className="shrink-0 opacity-80"
            />
            <span
              className="text-[11px] font-semibold truncate min-w-0 flex-1"
              style={{ color: textColor }}
            >
              {appointment?.patientName}
            </span>
            {!isMonthView ? (
              <span
                className={`inline-flex items-center rounded px-1 py-px text-[9px] font-bold uppercase tracking-wide border shrink-0 leading-none ${
                  PATIENT_TYPE_STYLES[patientTypeLabel] ||
                  PATIENT_TYPE_STYLES["Existing Patient"]
                }`}
              >
                {patientTypeShort}
              </span>
            ) : null}
          </div>

          {!isMonthView && metaParts.length > 0 ? (
            <div
              className="min-w-0 truncate text-[9px] font-medium opacity-95"
              style={{ color: textColor }}
            >
              {metaParts.join(" · ")}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
